import React, { useState, useEffect } from 'react';
import { Clock, Loader2, AlertTriangle, CheckCircle, Ban } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OvertimeRequestModal = ({ 
  employeeId, 
  employeeName, 
  locationId, 
  requestedTime, 
  scheduledStart, 
  minutesEarly, 
  onClose, 
  onRequestSubmitted,
  onStartAnyway
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isStartingAnyway, setIsStartingAnyway] = useState(false);
  const [isLoadingLimits, setIsLoadingLimits] = useState(true);
  const [requestId, setRequestId] = useState(null);
  const [overtimeInfo, setOvertimeInfo] = useState(null);
  const [limitReached, setLimitReached] = useState(false);

  const requestedOvertimeHours = (minutesEarly / 60).toFixed(2);

  useEffect(() => {
    fetchOvertimeLimits();
  }, []);

  const fetchOvertimeLimits = async () => {
    try {
      const response = await axios.get(`${API}/attendance/overtime-info/${employeeId}`, {
        params: { location_id: locationId }
      });
      
      if (response.data.success) {
        setOvertimeInfo(response.data);
        const { weekly_limit, current_overtime } = response.data;
        if (weekly_limit !== null && weekly_limit > 0) {
          const remaining = weekly_limit - current_overtime;
          const requestedHours = minutesEarly / 60;
          if (requestedHours > remaining) {
            setLimitReached(true);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching overtime limits:', error);
    } finally {
      setIsLoadingLimits(false);
    }
  };

  const handleRequestApproval = async () => {
    setIsSubmitting(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      
      // First, create the overtime request
      const response = await axios.post(`${API}/attendance/overtime-request`, {
        employee_id: employeeId,
        location_id: locationId,
        employee_name: employeeName,
        requested_checkin_time: requestedTime,
        scheduled_start: scheduledStart,
        date: currentDate,
        request_type: 'approval'
      });

      if (response.data.success) {
        setRequestId(response.data.request_id);
        
        // NOW ALSO CHECK THE EMPLOYEE IN - they shouldn't have to wait
        // The overtime status will be "pending" until approved
        try {
          const checkinResponse = await axios.post(`${API}/attendance/start-with-pending-approval`, {
            employee_id: employeeId,
            location_id: locationId,
            employee_name: employeeName,
            requested_checkin_time: requestedTime,
            scheduled_start: scheduledStart,
            date: currentDate,
            overtime_request_id: response.data.request_id
          });
          
          if (checkinResponse.data.success) {
            toast.success('Checked in! Approval request sent to manager.');
            // Refresh after brief delay to show the check-in
            setTimeout(() => {
              if (onStartAnyway) {
                onStartAnyway(response.data.request_id, true);
              } else {
                onClose();
                window.location.reload();
              }
            }, 1000);
            return;
          }
        } catch (checkinError) {
          // If check-in fails, still show the request was submitted
          console.error('Check-in after request failed:', checkinError);
          toast.success('Approval request submitted! You can start working.');
          if (onRequestSubmitted) onRequestSubmitted();
          setIsPolling(true);
          pollForApproval(response.data.request_id);
        }
      }
    } catch (error) {
      console.error('Error submitting overtime request:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit overtime request';
      if (errorMsg.includes('WEEKLY_LIMIT_REACHED')) {
        setLimitReached(true);
        toast.error('Weekly overtime limit reached');
      } else {
        toast.error(errorMsg);
      }
      setIsSubmitting(false);
    }
  };

  const handleStartAnyway = async () => {
    setIsStartingAnyway(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await axios.post(`${API}/attendance/start-overtime-unauthorised`, {
        employee_id: employeeId,
        location_id: locationId,
        employee_name: employeeName,
        requested_checkin_time: requestedTime,
        scheduled_start: scheduledStart,
        date: currentDate
      });

      if (response.data.success) {
        toast.success('Checked in successfully! Early time logged as unauthorised overtime.');
        if (onStartAnyway) {
          onStartAnyway(response.data.overtime_record_id, response.data.checked_in);
        } else {
          onClose();
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error starting unauthorised overtime:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to start';
      if (errorMsg.includes('WEEKLY_LIMIT_REACHED')) {
        setLimitReached(true);
        toast.error('Weekly overtime limit reached');
      } else {
        toast.error(errorMsg);
      }
      setIsStartingAnyway(false);
    }
  };

  const pollForApproval = async (reqId) => {
    const checkApproval = async () => {
      try {
        const response = await axios.get(`${API}/attendance/overtime-status/${employeeId}`, {
          params: { location_id: locationId }
        });
        if (response.data.has_approval) {
          setIsPolling(false);
          toast.success('Overtime approved! You may now check in.');
          setTimeout(() => window.location.reload(), 2000);
        }
      } catch (error) {
        console.error('Error checking approval status:', error);
      }
    };

    const interval = setInterval(checkApproval, 3000);
    setTimeout(() => {
      clearInterval(interval);
      if (isPolling) {
        setIsPolling(false);
        toast.info('Request is pending. You can start anyway or wait for approval.');
      }
    }, 300000);
    return () => clearInterval(interval);
  };

  // Limit reached state
  if (limitReached && !isLoadingLimits) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in fade-in zoom-in duration-300">
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-rose-500/15 rounded-full flex items-center justify-center mb-4">
              <Ban className="w-12 h-12 text-rose-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">Cannot Check In Early</h2>
            <p className="text-zinc-400 mb-4">You have met your weekly overtime limit.</p>
            
            {overtimeInfo && overtimeInfo.weekly_limit && (
              <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 mb-6 w-full">
                <p className="text-sm text-rose-300">
                  <span className="font-semibold">Weekly Limit:</span> {overtimeInfo.weekly_limit} hours
                </p>
                <p className="text-sm text-rose-300">
                  <span className="font-semibold">Used:</span> {overtimeInfo.current_overtime?.toFixed(2)} hours
                </p>
                <p className="text-sm text-rose-300">
                  <span className="font-semibold">Remaining:</span> 0 hours
                </p>
              </div>
            )}
            
            <p className="text-sm text-zinc-500 mb-6">
              Your overtime allowance resets every {overtimeInfo?.week_start_day || 'Monday'}.
            </p>
            
            <button
              onClick={onClose}
              data-testid="overtime-limit-close-btn"
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-semibold py-3 px-6 rounded-xl transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in fade-in zoom-in duration-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-amber-500/15 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-12 h-12 text-amber-400" />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">You're Arriving Early</h2>
          
          <p className="text-zinc-400 mb-1">Your shift starts at <span className="font-semibold text-white">{scheduledStart}</span>.</p>
          <p className="text-zinc-400 mb-1">Current time: <span className="font-semibold text-white">{requestedTime}</span></p>
          <p className="text-zinc-400 mb-4">You are <span className="font-semibold text-amber-400">{minutesEarly} minutes early</span>.</p>
          
          {/* Weekly Overtime Limit Info */}
          {isLoadingLimits ? (
            <div className="flex items-center gap-2 text-zinc-500 mb-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading overtime info...</span>
            </div>
          ) : overtimeInfo && overtimeInfo.weekly_limit !== null ? (
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3 mb-4 w-full text-left">
              <p className="text-sm text-violet-300 font-medium mb-1">Weekly Overtime Allowance</p>
              <p className="text-xs text-violet-300/80">
                <span className="font-semibold">Limit:</span> {overtimeInfo.weekly_limit} hours/week
              </p>
              <p className="text-xs text-violet-300/80">
                <span className="font-semibold">Used:</span> {overtimeInfo.current_overtime?.toFixed(2)} hours
              </p>
              <p className="text-xs text-violet-300/80">
                <span className="font-semibold">Remaining:</span> {(overtimeInfo.weekly_limit - overtimeInfo.current_overtime).toFixed(2)} hours
              </p>
              <p className="text-xs text-amber-400 mt-2 font-medium">
                Starting now will use {requestedOvertimeHours} hours of your allowance
              </p>
            </div>
          ) : (
            <p className="text-xs text-zinc-500 mb-4">No weekly overtime limit set</p>
          )}
          
          <p className="text-sm text-zinc-500 mb-4">Choose an option:</p>
          
          {!isSubmitting && !isPolling && !isStartingAnyway && (
            <div className="w-full space-y-3">
              <button
                onClick={handleRequestApproval}
                data-testid="overtime-request-approval-btn"
                className="w-full bg-violet-500/20 hover:bg-violet-500/30 text-violet-200 ring-1 ring-violet-400/25 font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Request Approval
              </button>
              
              <button
                onClick={handleStartAnyway}
                data-testid="overtime-start-anyway-btn"
                className="w-full bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 ring-1 ring-amber-400/25 font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <AlertTriangle className="w-5 h-5" />
                Start Anyway
              </button>
              <p className="text-xs text-amber-400/70 font-medium">
                Starting without approval may be marked as unauthorised overtime
              </p>
              
              <button
                onClick={onClose}
                data-testid="overtime-cancel-btn"
                className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-semibold py-3 px-6 rounded-xl transition-colors mt-2"
              >
                Cancel
              </button>
            </div>
          )}

          {isSubmitting && (
            <div className="w-full">
              <div className="flex items-center justify-center gap-2 text-violet-400 mb-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Submitting request...</span>
              </div>
            </div>
          )}

          {isPolling && (
            <div className="w-full">
              <div className="flex items-center justify-center gap-2 text-violet-400 mb-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Waiting for manager approval...</span>
              </div>
              <p className="text-sm text-zinc-500 mb-4">This page will refresh automatically when approved.</p>
              
              <button
                onClick={handleStartAnyway}
                className="w-full bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 ring-1 ring-amber-400/25 font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <AlertTriangle className="w-5 h-5" />
                Start Anyway (may be unauthorised)
              </button>
              <p className="text-xs text-amber-400/70 mt-2">
                You can start working while waiting. If approved later, hours will be authorised.
              </p>
            </div>
          )}

          {isStartingAnyway && (
            <div className="w-full">
              <div className="flex items-center justify-center gap-2 text-amber-400 mb-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Starting early...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OvertimeRequestModal;
